import URLSearchParams from '@ungap/url-search-params';
import { History } from 'history';
import React, { useEffect, useState } from 'react';
import {
    FormattedMessage,
    WrappedComponentProps,
    injectIntl,
    useIntl,
} from 'react-intl';
import { connect } from 'react-redux';
import { matchesNameCode } from '../../common';
import { Navigation } from '../../global-functions';
import { RootState } from '../../store';
import { IConstants, ISession } from '../../models/pro-sites';
import { IProfile } from '../../models/profile';
import { setIsBusy } from '../../store/api/api-status-action';
import { loadConstants } from '../../store/externals/externals-actions';
import { loadProfile, saveProfile } from '../../store/profile/profile-actions';
import { FieldValidation, validate } from '../../validation/validation';
import * as Validators from '../../validation/validators';
import PageTitle from '../common/page.title';
import DataAuthorizationModal from './data-authorization-modal';
import ProfileForm, { ProfileFormErrors } from './profile-form';
import { getErrorMessage } from '../../utils/error-handling';

interface IMyProfileProps {
    profile: IProfile | null;
    session: ISession | null;
    constants: IConstants | null;
    loadProfile: () => Promise<void>;
    saveProfile: (profile: IProfile, saveConsent: boolean) => Promise<boolean>;
    loadConstants: () => Promise<void>;
    setIsBusy: (isBusy: boolean) => Promise<void>;
    location: Location;
    history: History;
    saveError: string | null;
}

export const MyProfile = ({
    profile,
    session,
    constants,
    loadProfile,
    saveProfile,
    loadConstants,
    setIsBusy,
    location,
    history,
    saveError,
}: IMyProfileProps) => {
    const intl = useIntl();
    const [errorsState, setErrorsState] = useState({});
    const [saving, setSaving] = useState(false);
    const [missingDataAuth, setMissingDataAuth] = useState(false);
    const [incomplete, setIncomplete] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [profileState, setProfileState] = useState({ ...profile! });

    const fieldValidations = [
        new FieldValidation('firstName', 'First Name', [
            Validators.required(
                intl.formatMessage(
                    {
                        id: 'Required_Field_Message',
                        defaultMessage: '{0} is required',
                    },
                    {
                        0: intl.formatMessage({ id: 'First_Name' }),
                    }
                )
            ),
        ]),
        new FieldValidation('lastName', 'Last Name', [
            Validators.required(
                intl.formatMessage(
                    {
                        id: 'Required_Field_Message',
                        defaultMessage: '{0} is required',
                    },
                    {
                        0: intl.formatMessage({ id: 'Last_Name' }),
                    }
                )
            ),
        ]),
        new FieldValidation('phoneNumber', 'Phone Number', [
            Validators.required(),
        ]),
        new FieldValidation('street1', 'My Address', [Validators.required()]),
        new FieldValidation('city', 'City', [Validators.required()]),
        new FieldValidation('state', 'State/Province', [Validators.required()]),
        new FieldValidation('country', 'Country', [Validators.required()]),
        new FieldValidation('cellNumber', 'Cell Number', [
            Validators.required(),
        ]),
        new FieldValidation('otherNotificationEmail', ' ', [
            Validators.isEmail(
                intl.formatMessage({ id: 'Invalid_Email_Address' })
            ),
        ]),
    ];

    const formIsValid = (): boolean => {
        const validations = !profileState.smsFlag
            ? fieldValidations.filter((x) => x.name !== 'cellNumber')
            : fieldValidations;

        const errors: ProfileFormErrors = validate(profileState, validations);

        setErrorsState(errors);

        return Object.keys(errors).length === 0;
    };

    useEffect(() => {
        loadProfile();

        const params = new URLSearchParams(location.search);
        const isIncomplete = params.get('incomplete') === 'true';
        setIncomplete(isIncomplete);

        const isMissingDataAuth = params.get('missingDataAuthCO') === 'true';
        setMissingDataAuth(isMissingDataAuth);
    }, []);

    useEffect(() => {
        if (saving) {
            return;
        }

        if (profile) {
            // necessary because we might be updating the country code or state code and that causes the profile prop to mutate.

            const emailFlag =
                profile.emailFlag === null ? true : profile.emailFlag;

            setProfileState({ ...profile, emailFlag: emailFlag });

            if (missingDataAuth) {
                setShowModal(formIsValid());
            }
        }
    }, [profile]);

    useEffect(() => {
        if (!constants) {
            loadConstants();
        }
    }, [constants]);

    const handleModalClose = () => {
        setShowModal(false);
    };

    useEffect(() => {
        if (saveError) {
            setErrorsState((prevState) => ({
                ...prevState,
                onSave: saveError!,
            }));
        }
    }, [saveError]);

    const handleChange = (event: any) => {
        const { name, value, checked } = event.target;

        setProfileState((prevState) => ({
            ...prevState,
            [name]:
                name === 'emailFlag' || name === 'smsFlag' ? checked : value,
        }));
    };

    const save = async (patientConsent: boolean) => {
        setSaving(true);

        try {
            const profileToSave = { ...profileState };
            const ok = await saveProfile(profileToSave, patientConsent);

            if (ok) {
                if (session!.siteNumber < 5 && !profileToSave.patientConsent) {
                    history.push('/my-account/patient-consent');
                } else {
                    setIsBusy(true);
                    Navigation.pushExternal('/my-account/account-summary');
                }
            }
        } catch (error) {
            setSaving(false);
            setErrorsState({ onSave: getErrorMessage(error) });
        }
    };

    const handleModalAccept = async () => {
        setShowModal(false);
        setMissingDataAuth(false);

        save(true);
    };

    const handleSave = async (event: React.ChangeEvent) => {
        event.preventDefault();
        if (!formIsValid()) {
            return;
        }

        if (session!.country === 'CO') {
            setShowModal(true);
            return;
        }

        save(false);
    };

    return (
        <>
            {Object.keys(profileState).length > 0 && session && constants && (
                <>
                    <PageTitle pageTitleId="My_Profile" />
                    {incomplete && (
                        <div className="alert alert-warning gutter-bottom">
                            <h3 style={{ border: 'none' }}>
                                <FormattedMessage id="Please_Complete_Your_Profile" />
                            </h3>
                            <p>
                                <FormattedMessage id="Please_complete_all_required" />
                            </p>
                        </div>
                    )}
                    <h1>
                        <FormattedMessage id="My_Profile" />
                    </h1>
                    <ProfileForm
                        profile={profileState}
                        session={session}
                        constants={constants!}
                        saving={saving}
                        errors={errorsState}
                        onChange={handleChange}
                        onSave={handleSave}
                    />
                    {showModal && (
                        <DataAuthorizationModal
                            onClose={handleModalClose}
                            onAccept={handleModalAccept}
                        />
                    )}
                </>
            )}
        </>
    );
};

const mapProfileFromState = (
    profile: IProfile | null,
    session: ISession | null,
    constants: IConstants | null
): IProfile | null => {
    if (!profile || !session || !constants) {
        return profile!;
    }

    const value = profile.country || session.country;
    const found = constants.countries.find(matchesNameCode(value));

    return found && profile.country === found.code
        ? profile
        : { ...profile, country: found ? found.code : undefined };
};

const mapStateToProps = (state: RootState) => {
    return {
        profile: mapProfileFromState(
            state.profile ? state.profile.data! : null,
            state.session ? state.session.data! : null,
            state.constants
        ),
        session: state.session ? state.session.data! : null,
        constants: state.constants,
        saveError:
            state.currentUser && state.currentUser.message
                ? state.currentUser.message!
                : null,
    };
};

const mapDispatchToProps = {
    loadProfile,
    saveProfile,
    loadConstants,
    setIsBusy,
};

const myProfileContainer = connect(
    mapStateToProps,
    mapDispatchToProps
)(React.memo(MyProfile));

export default myProfileContainer;
